import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import type { SeatMapDto, SeatMapLocalityDto, SeatMapSeatDto } from '@/lib/api/taquilla';
import { Button, Card, CardTitle, toast } from '@/components/ui';
import { useCart } from '@/store/cart';
import { useMoney } from '@/hooks/useMoney';
import { SeatMapCanvas } from './SeatMapCanvas';
import { localityColor } from './localityColor';

/**
 * SeatMapSection — flujo de compra POR SILLA sobre el mapa SVG (F5).
 *
 * Combina el lienzo (`SeatMapCanvas`) con un panel de selección: el cliente
 * toca asientos en el mapa, los ve listados con precio, y al "Agregar al
 * carrito" se crean N líneas por-silla (una por asiento, con su localidad y
 * seat_id). Los datos del titular se piden DESPUÉS, en el carrito (CartReview),
 * tal como pidió el Operator ("ponerle todo al detalle antes de confirmar").
 */
type Props = {
  seatMap: SeatMapDto;
  eventId: string;
  /** stage_id del evento (el carrito de taquilla compra por stage). */
  stageId: string | null;
  currency?: string | null;
};

type Picked = { seat: SeatMapSeatDto; locality: SeatMapLocalityDto };

export function SeatMapSection({ seatMap, eventId, stageId, currency }: Props): JSX.Element {
  const { t } = useTranslation(['events', 'common']);
  const { money } = useMoney();
  const navigate = useNavigate();
  const cart = useCart();

  // seat_id → {seat, locality}
  const [picked, setPicked] = useState<Map<string, Picked>>(new Map());

  // Índice de localidad (para color consistente con el lienzo) por id.
  const locIndex = useMemo(() => {
    const seated = seatMap.localities.filter((l) => (l.seats?.length ?? 0) > 0);
    const m = new Map<string, number>();
    seated.forEach((l, i) => m.set(l.id, i));
    return m;
  }, [seatMap.localities]);

  function toggleSeat(seat: SeatMapSeatDto, locality: SeatMapLocalityDto): void {
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(seat.id)) next.delete(seat.id);
      else next.set(seat.id, { seat, locality });
      return next;
    });
  }

  const items = [...picked.values()];
  const total = items.reduce((sum, p) => sum + (p.locality.price_cop ?? 0), 0);

  function addToCart(): void {
    if (!stageId || items.length === 0) return;
    cart.setEvent(eventId);
    for (const { seat, locality } of items) {
      cart.addLine({
        eventId,
        stageId,
        localityId: locality.id,
        seatIdentifier: seat.id,
        holderName: '',
        quantity: 1,
      });
    }
    toast.success({
      title: t('seatMap.addedToCart', 'Agregado al carrito'),
      description: t('seatMap.addedSeats', {
        defaultValue: '{{count}} asiento(s) — completá los datos en el carrito',
        count: items.length,
      }),
    });
    setPicked(new Map());
    navigate('/cart');
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
      {/* Mapa */}
      <SeatMapCanvas
        seatMap={seatMap}
        selected={new Set(picked.keys())}
        onToggleSeat={toggleSeat}
        currency={currency}
      />

      {/* Panel de selección */}
      <Card variant="default" padded className="h-fit lg:sticky lg:top-4">
        <CardTitle>{t('seatMap.yourSelection', 'Tu selección')}</CardTitle>
        {items.length === 0 ? (
          <p className="mt-3 text-sm text-fc-muted">
            {t('seatMap.pickPrompt', 'Tocá los asientos disponibles en el mapa para elegirlos.')}
          </p>
        ) : (
          <>
            <ul className="mt-3 flex flex-col divide-y divide-fc-line">
              {items.map(({ seat, locality }) => (
                <li key={seat.id} className="flex items-center justify-between gap-2 py-2">
                  <span className="inline-flex min-w-0 items-center gap-2">
                    <span
                      className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{
                        backgroundColor: localityColor(locality, locIndex.get(locality.id) ?? 0),
                      }}
                    />
                    <span className="truncate text-sm text-fc-ink">
                      {locality.name}
                      {seat.row && seat.number
                        ? ` · Fila ${seat.row} · ${seat.number}`
                        : seat.label
                          ? ` · ${seat.label}`
                          : ''}
                    </span>
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="text-sm tabular-nums text-fc-ink">
                      {money(locality.price_cop ?? 0, currency)}
                    </span>
                    <button
                      type="button"
                      aria-label={t('seatMap.removeSeat', 'Quitar asiento')}
                      className="text-fc-muted hover:text-fc-ink"
                      onClick={() => toggleSeat(seat, locality)}
                    >
                      ✕
                    </button>
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex items-center justify-between border-t border-fc-line pt-3">
              <span className="text-sm text-fc-muted">
                {t('seatMap.totalSeats', {
                  defaultValue: '{{count}} asiento(s)',
                  count: items.length,
                })}
              </span>
              <span className="text-lg font-semibold text-fc-ink">{money(total, currency)}</span>
            </div>
            <Button className="mt-4 w-full" disabled={!stageId} onClick={addToCart}>
              {t('seatMap.addToCart', 'Agregar al carrito')}
            </Button>
            {!stageId && (
              <p className="mt-2 text-xs text-fc-accent">
                {t('seatMap.noStage', 'Este evento aún no tiene una etapa de venta activa.')}
              </p>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
